<script setup lang="ts">
/**
 * MY — Incorporation DOC (M32014) Studio panel.
 * Live document: Section 14 + Section 201 (± attestation) — Director resignation 패턴.
 */
import { ref, reactive, computed, provide, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { CDD_DOC_CTX } from './cdd/cddContext';
import {
  progressKeysForRows,
  DOC_SHEET_PATTERNS,
} from './incorporation/incorporationDocFields';
// M32100 통합: typeOf/labelize 는 incorporation+CDD 키를 모두 처리하는 병합 버전.
import { typeOf, labelize, isCddKey } from './incorporation-pre/incPreFields';
import { INC_PRE_CDD, CDD_TYPE_META, type CddInstance } from './incorporation-pre/incPreCddContext';
import { INC_PRE_S201, S201_TYPE_META, type S201Instance, type S201DocType } from './incorporation-pre/incPreS201Context';
import { CDD_DOCS, type CddDocType } from './cdd/cddFields';
import IncorporationPreDoc from './incorporation-pre/IncorporationPreDoc.vue';
import {
  buildIncorporationDocFormData,
  isForeignDirector,
} from '../mappers/incorporationDocMapper';
import { mapIncorporationToIncDocValues } from '../mappers/mapIncorporationToIncDocValues';
import { autoMapVariables } from '@/features/startTask/preset_task/my/incorporation/composables/incorporationVariableMapper';
import { parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import { comboSourceOf, addressGroupOf, CDD_SHAREHOLDER_MAX } from './cdd/cddFields';
import { buildCddVariables } from '../mappers/cddMapper';
import { comboOptions, resolveIdType, resolveRace } from './cdd/cddOptions';
import { SHARE_CLASS_OPTIONS, buildShareholderRows, buildBoRows } from './cdd/cddShareholders';
import { getActiveMembers, getActiveBeneficialOwners } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { useSessionStore } from '@/shared/stores/sessionStore';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const docRef = ref<InstanceType<typeof IncorporationPreDoc> | null>(null);
const values = reactive<Record<string, string>>({}); // 공유(Section 14/201 + 회사) 값
const activeKey = ref('');

// ── T-Link(incorporation) 인물 — CDD ✨ lookup 에 주입(엔티티 임원 테이블엔 없으므로) ──
const tlinkPersons = ref<any[]>([]);
// ✨ 선택 시 autoMapVariables 재실행용 컨텍스트(자동생성과 동일 수준으로 채움).
const cddMapCtx = ref<{ incData: any; qa: any; schemas: Record<string, any> } | null>(null);

// ── CDD 인스턴스(사람/클라이언트별) 리스트 ──
const cddDocs = ref<CddInstance[]>([]);
const activeCddId = ref(0);
let cddSeq = 0;
const activeCdd = computed(() => cddDocs.value.find((d) => d.id === activeCddId.value) || null);
/** 키 네임스페이스별 저장소 라우팅 — CDD / Section201 인스턴스 / 공유. */
function bag(key: string): Record<string, string> {
  if (isCddKey(key) && activeCdd.value) return activeCdd.value.values;
  if (isS201Key(key) && activeS201.value) return activeS201.value.values;
  return values;
}
function addCdd(docType: CddDocType) {
  const id = ++cddSeq;
  cddDocs.value.push({ id, docType, label: CDD_DOCS[docType]?.label || CDD_TYPE_META[docType].label, values: {}, checkbox: '', shRowCount: 2, boRowCount: 2 });
  activeCddId.value = id;
}
function removeCdd(id: number) {
  const i = cddDocs.value.findIndex((d) => d.id === id);
  if (i < 0) return;
  cddDocs.value.splice(i, 1);
  if (activeCddId.value === id) activeCddId.value = cddDocs.value[Math.max(0, i - 1)]?.id || 0;
}
function selectCdd(id: number) { activeCddId.value = id; }
provide(INC_PRE_CDD, { docs: cddDocs, activeId: activeCddId, select: selectCdd, add: addCdd, remove: removeCdd });

// ── Section 201 인스턴스(선임 이사별) 리스트 ──
const s201Docs = ref<S201Instance[]>([]);
const activeS201Id = ref(0);
let s201Seq = 0;
const activeS201 = computed(() => s201Docs.value.find((d) => d.id === activeS201Id.value) || null);
const isS201Key = (key: string) => key === 's201_name' || key === 's201_nric';
function addS201(docType: S201DocType) {
  const id = ++s201Seq;
  s201Docs.value.push({ id, docType, label: S201_TYPE_META[docType].label, values: {} });
  activeS201Id.value = id;
}
function removeS201(id: number) {
  const i = s201Docs.value.findIndex((d) => d.id === id);
  if (i < 0) return;
  s201Docs.value.splice(i, 1);
  if (activeS201Id.value === id) activeS201Id.value = s201Docs.value[Math.max(0, i - 1)]?.id || 0;
}
function selectS201(id: number) { activeS201Id.value = id; }
provide(INC_PRE_S201, { docs: s201Docs, activeId: activeS201Id, select: selectS201, add: addS201, remove: removeS201 });

const activeCheckbox = ref('');
watch(activeCddId, () => { activeCheckbox.value = activeCdd.value?.checkbox || ''; });
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

function setFieldValue(key: string, val: string) {
  bag(key)[key] = val;
}
function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'msic' || /^business_nature_\d+$/.test(key) || /^msic_code_\d+$/.test(key)) {
    const slot =
      key.match(/^(?:business_nature|msic_code)_(\d+)$/)?.[1] || '1';
    emit('request-person-lookup', {
      fieldGroup: 'incorporation-doc',
      key: `business_nature_${slot}`,
      lookupType: 'msic',
      msicSlot: Number(slot),
    });
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    // CDD 이름/인물 필드 → T-Link 인물 전체(ENTITY_OFFICER) 를 챗봇에 띄워 선택 → 자동바인딩.
    //   (lp_company_name/la_name 은 entity 라 원래 회사목록으로 새지만, CDD 는 인물 목록이어야 함)
    const cdd = isCddKey(key);
    // dir/mem 이름 + CDD 이름 필드 → T-Link 인물 전체 주입(엔티티 임원 테이블엔 없으므로). company_name 은 회사목록.
    const wantPersons = cdd || /^(dir|mem)\d+_name$/.test(key) || key === 's201_name';
    const defaultRole = wantPersons
      ? 'ENTITY_OFFICER'
      : t === 'person'
        ? 'DIRECTOR'
        : null;
    emit('request-person-lookup', {
      fieldGroup: 'incorporation-doc',
      key,
      defaultRole,
      // Proposed company name → incorporation 테이블 목록. 인물(dir/mem/CDD) 은 인물 목록.
      lookupType: !wantPersons && (key === 'company_name' || t === 'entity') ? 'incorporation' : 'officer',
      // T-Link 인물 목록을 직접 주입(dir/mem/CDD 공통).
      preloadedOfficers: wantPersons ? tlinkPersons.value : undefined,
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = bag(key)[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) bag(inputKey.value)[inputKey.value] = inputText.value.trim();
  inputKey.value = '';
  activeKey.value = '';
}
function skipInput() {
  inputKey.value = '';
  activeKey.value = '';
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{
  line?: string;
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
}>({});

function openAddress(key: string) {
  if (props.studio) {
    addrKey.value = key;
    emit('request-address-input', key);
    return;
  }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: bag(key)[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(
  key: string,
  a: { line: string; town: string; state: string; postcode: string; country: string },
) {
  if (!key) return;
  bag(key)[key] = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ')
    .toUpperCase();
}
function applyAddress(a: {
  line: string;
  town: string;
  state: string;
  postcode: string;
  country: string;
}) {
  applyAddressTo(addrKey.value, a);
}
watch(
  () => props.appliedAddress,
  (a) => {
    if (a?.key) applyAddressTo(a.key, a);
  },
);

function comboOptionsOf(key: string) {
  if (/share_type_\d+$/.test(key)) return SHARE_CLASS_OPTIONS;
  return comboOptions(comboSourceOf(key));
}
const pendingPickValue = ref('');

// ── CDD Legal Person 주주/BO 행관리 (활성 CDD 인스턴스 기반) ──
const shRowCountRef = computed(() => activeCdd.value?.shRowCount ?? 2);
const boRowCountRef = computed(() => activeCdd.value?.boRowCount ?? 2);
function setCheckbox(v: string) {
  const nv = activeCheckbox.value === v ? '' : v;
  activeCheckbox.value = nv;
  if (activeCdd.value) activeCdd.value.checkbox = nv;
}
function addShareholderRow() {
  const d = activeCdd.value; if (!d) return;
  const cur = Math.max(1, d.shRowCount || 1);
  if (cur >= CDD_SHAREHOLDER_MAX) return;
  d.shRowCount = cur + 1;
}
function removeShareholderRow(index: number) {
  const d = activeCdd.value; if (!d) return; const v = d.values;
  const total = Math.max(1, d.shRowCount || 1);
  if (index < 1 || index > total) return;
  if (total <= 1) { v.shareholder_name_1 = ''; v.share_type_1 = ''; v.share_pct_1 = ''; return; }
  for (let i = index; i < total; i++) {
    v[`shareholder_name_${i}`] = v[`shareholder_name_${i + 1}`] || '';
    v[`share_type_${i}`] = v[`share_type_${i + 1}`] || '';
    v[`share_pct_${i}`] = v[`share_pct_${i + 1}`] || '';
  }
  v[`shareholder_name_${total}`] = ''; v[`share_type_${total}`] = ''; v[`share_pct_${total}`] = '';
  d.shRowCount = total - 1;
}
function addBoRow() {
  const d = activeCdd.value; if (!d) return;
  const cur = Math.max(1, d.boRowCount || 1);
  if (cur >= CDD_SHAREHOLDER_MAX) return;
  d.boRowCount = cur + 1;
}
function removeBoRow(index: number) {
  const d = activeCdd.value; if (!d) return; const v = d.values;
  const total = Math.max(1, d.boRowCount || 1);
  if (index < 1 || index > total) return;
  if (total <= 1) { v.bo_name_1 = ''; v.bo_relationship_1 = ''; return; }
  for (let i = index; i < total; i++) {
    v[`bo_name_${i}`] = v[`bo_name_${i + 1}`] || '';
    v[`bo_relationship_${i}`] = v[`bo_relationship_${i + 1}`] || '';
  }
  v[`bo_name_${total}`] = ''; v[`bo_relationship_${total}`] = '';
  d.boRowCount = total - 1;
}

provide(CDD_DOC_CTX, {
  valueOf: (k) => bag(k)[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  checkboxValue: activeCheckbox,
  setCheckbox,
  comboOptionsOf,
  shareholderRowCount: shRowCountRef,
  boRowCount: boRowCountRef,
  addShareholderRow,
  removeShareholderRow,
  addBoRow,
  removeBoRow,
  pendingPickValue,
  placePick: () => false,
});

/** CDD 인물 필드(✨ 선택) → 활성 CDD 인스턴스 자동바인딩.
 *  자동생성과 동일하게 autoMapVariables 를 재실행해 주소·국가 등 전부 채운다. */
function applyPersonToCdd(key: string, a: any, name: string) {
  const inst = activeCdd.value;
  const v = bag(key);
  v[key] = name;

  // 메인 이름 필드(ind_full_name / lp_company_name / la_name)면 autoMap 전체 채움.
  const isMainName = key === 'ind_full_name' || key === 'lp_company_name' || key === 'la_name';
  const ctx = cddMapCtx.value;
  if (inst && isMainName && ctx) {
    const nrm = (n: string) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const nn = nrm(name);
    let context: 'director' | 'shareholder' = 'director'; let idx = 0;
    const di = (ctx.qa.directors || []).findIndex((d: any) => nrm(d.name) === nn);
    if (di >= 0) { context = 'director'; idx = di; }
    else { const si = (ctx.qa.shareholders || []).findIndex((s: any) => nrm(s.name) === nn); if (si >= 0) { context = 'shareholder'; idx = si; } }
    const schema = ctx.schemas[inst.docType];
    if (schema) {
      try {
        const fd = autoMapVariables(schema, ctx.incData, ctx.qa, context as any, idx);
        for (const [k, val] of Object.entries(fd)) { if (val && typeof val === 'string' && !val.startsWith('[')) v[k] = val; }
      } catch (e) { console.warn('[IncorporationPre] CDD applyPerson autoMap failed:', e); }
    }
  }

  // autoMap 이 못 채운 필드 보충(선택 인물 객체 직접 매핑).
  const idNo = a.nricNo || a.passportNo || a.contactRegNo || a.idNo || a.registrationNo || a.identificationNo || '';
  const nat = a.nationality || a.nationalityName || '';
  const dob = a.dateOfBirth || a.dob || '';
  const addr = a.residentialAddress || a.primaryAddress || a.registeredAddress || a.address || '';
  const phone = a.contactNo || a.mobileNo || a.phone || a.phoneNo || '';
  const set = (k: string, val: unknown) => { const s = String(val || '').trim(); if (s && !String(v[k] || '').trim()) v[k] = s; };
  if (key === 'ind_full_name') {
    set('ind_ic_passport_no', idNo); set('ind_nationality', nat); set('ind_dob', dob); set('ind_residential_address', addr); set('ind_contact_no', phone);
  } else if (key === 'lp_company_name') {
    set('lp_registration_no', idNo); set('lp_registered_address', addr); set('lp_contact_no', phone); set('lp_country_of_incorporation', nat);
  } else if (key === 'la_name') {
    set('la_registration_no', idNo); set('la_registered_address', addr); set('la_contact_no', phone); set('la_country_of_registration', nat);
  }
}

/** dir/mem 이름(✨ 선택) → 초기 autofill 이 이미 채운 그 인물의 행(dir<i>_/mem<i>_) 값을 대상 행으로 복사(gender 포함 전부). */
function applyPersonToIncRow(key: string, a: any): boolean {
  const m = key.match(/^(dir|mem)(\d+)_name$/);
  if (!m) return false;
  const kind = m[1]; const dst = `${kind}${m[2]}_`;
  const nrm = (n: string) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const nn = nrm(a.name || a.contactName || a.corporateName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' '));
  try {
    // 선택 인물 → tlinkPersons 에서 초기 autofill 행 prefix 찾기(직접 넘어온 _srcPrefix 우선, 없으면 이름 매칭).
    let src = String(a._srcPrefix || '');
    if (!src) src = String(tlinkPersons.value.find((p: any) => nrm(p.name) === nn && String(p._srcPrefix).startsWith(kind))?._srcPrefix || tlinkPersons.value.find((p: any) => nrm(p.name) === nn)?._srcPrefix || '');
    if (src && src !== dst) {
      for (const k of Object.keys(values)) {
        if (k.startsWith(src) && String(values[k] || '').trim()) values[dst + k.slice(src.length)] = values[k];
      }
      return true;
    }
    // 초기 autofill 에 없던 인물(수동 매핑) → 단일 인물 매퍼로 채움.
    const single: any = kind === 'dir' ? { directors: [a] } : { shareholders: [a] };
    const mapped = mapIncorporationToIncDocValues(single, {});
    const s1 = kind === 'dir' ? 'dir1_' : 'mem1_';
    for (const [k, val] of Object.entries(mapped.values)) {
      if (k.startsWith(s1) && val) values[dst + k.slice(s1.length)] = val as string;
    }
  } catch (e) {
    console.warn('[IncorporationPre] applyPersonToIncRow failed:', e);
    return false;
  }
  return true;
}

function applyPerson(key: string, a: any) {
  const name =
    a.name ||
    a.contactName ||
    [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') ||
    a.corporateName ||
    '';
  // CDD 필드면 활성 CDD 인스턴스에 매핑(자동바인딩) 후 종료.
  if (isCddKey(key)) { applyPersonToCdd(key, a, name); return; }
  // Section 201 인스턴스 이름 필드 → 활성 S201 인스턴스에 이름/NRIC 바인딩 후 종료.
  if (isS201Key(key)) {
    const tgt = activeS201.value?.values;
    if (tgt) {
      tgt.s201_name = name;
      // ID 후보 전체 + 초기 autofill 로 채워진 dir/mem 행값(_srcPrefix) 폴백.
      let idNo = a.nricNo || a.passportNo || a.contactRegNo || a.idNo
        || a.registrationNo || a.identificationNo || a.regNo || a.icNo || '';
      if (!idNo) {
        const norm = (s: any) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const nn = norm(name);
        const src = String(a._srcPrefix
          || tlinkPersons.value.find((p: any) => norm(p.name) === nn)?._srcPrefix || '');
        if (src) idNo = String(values[`${src}id_no`] || '');
      }
      if (idNo) tgt.s201_nric = String(idNo);
    }
    return;
  }
  values[key] = name;

  // dir/mem 이름 → 단일 인물을 매퍼에 넣어 전체(ID·국적·주소·gender·주식정보 등) 채운 뒤 해당 행으로 remap.
  if (applyPersonToIncRow(key, a)) return;

  if (typeOf(key) === 'entity' || key === 'company_name') {
    values.company_name = name || a.entityName || a.companyName || values.company_name || '';
    const fullAddr = a.registeredAddress || a.primaryAddress || a.residentialAddress || '';
    const res =
      a.myResidential && !isEmptyMyAddress(a.myResidential)
        ? a.myResidential
        : parseMalaysianAddress(fullAddr);
    if (!isEmptyMyAddress(res)) {
      values.registered_address = [
        res.street || fullAddr,
        [res.postcode, res.town].filter(Boolean).join(' '),
        res.state,
        res.country,
      ]
        .filter(Boolean)
        .join(', ');
    } else if (fullAddr) {
      values.registered_address = fullAddr;
    }
    if (a.email || a.contactEmail) values.registered_email = a.email || a.contactEmail;
    return;
  }

  const dirM = key.match(/^dir(\d+)_name$/);
  if (dirM) {
    const id = Number(dirM[1]);
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values[`dir${id}_id_no`] = idNo;
    if (a.nricNo && !a.passportNo) values[`dir${id}_id_type`] = resolveIdType('NRIC') || 'NRIC';
    else if (a.passportNo) values[`dir${id}_id_type`] = resolveIdType('Passport') || 'Passport';
    else if (a.idType || a.idTypeCd) {
      const t = resolveIdType(a.idType || a.idTypeCd);
      if (t) values[`dir${id}_id_type`] = t;
    }
    if (a.nationality || a.nationalityName) {
      values[`dir${id}_nationality`] = a.nationality || a.nationalityName;
    }
    if (a.dateOfBirth || a.dob) values[`dir${id}_dob`] = a.dateOfBirth || a.dob;
    if (a.email || a.contactEmail) values[`dir${id}_email`] = a.email || a.contactEmail;
    if (a.race) {
      const r = resolveRace(a.race);
      if (r) values[`dir${id}_race`] = r;
    }
    const fullAddr = a.residentialAddress || a.primaryAddress || '';
    const res =
      a.myResidential && !isEmptyMyAddress(a.myResidential)
        ? a.myResidential
        : parseMalaysianAddress(fullAddr);
    if (!isEmptyMyAddress(res)) {
      values[`dir${id}_address`] = [
        res.street || fullAddr,
        [res.postcode, res.town].filter(Boolean).join(' '),
        res.state,
        res.country,
      ]
        .filter(Boolean)
        .join(', ');
    } else if (fullAddr) {
      values[`dir${id}_address`] = fullAddr;
    }
    return;
  }

  const memM = key.match(/^mem(\d+)_name$/);
  if (memM) {
    const id = Number(memM[1]);
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values[`mem${id}_id_no`] = idNo;
    if (a.nricNo && !a.passportNo) values[`mem${id}_id_type`] = resolveIdType('NRIC') || 'NRIC';
    else if (a.passportNo) values[`mem${id}_id_type`] = resolveIdType('Passport') || 'Passport';
    else if (a.idType || a.idTypeCd) {
      const t = resolveIdType(a.idType || a.idTypeCd);
      if (t) values[`mem${id}_id_type`] = t;
    }
    if (a.nationality || a.nationalityName) {
      values[`mem${id}_nationality`] = a.nationality || a.nationalityName;
    }
    if (a.email || a.contactEmail) values[`mem${id}_email`] = a.email || a.contactEmail;
    if (a.race) {
      const r = resolveRace(a.race);
      if (r) values[`mem${id}_race`] = r;
    }
    const fullAddr = a.residentialAddress || a.primaryAddress || '';
    if (fullAddr) values[`mem${id}_address`] = fullAddr;
    return;
  }

  if (key === 'signer_name') {
    values.signer_name = name;
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values.signer_nric_passport_no = idNo;
  }
}

watch(
  () => props.selectedPerson,
  (p) => {
    if (!p) return;
    const ctx = props.activePersonContext;
    if (!ctx?.key) return;
    applyPerson(String(ctx.key || ''), p as any);
  },
);

watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    if (fd.taskTitle || fd._taskTitle) {
      titleModel.value = String(fd.taskTitle || fd._taskTitle);
    }
    // 회사가 링크되어 있을 때만 company_name 반영 — Change→Skip 후 이전 회사명이 다시 채워지는 것 방지
    if (fd.company_name && entityCd.value) {
      values.company_name = String(fd.company_name);
    }
    if (fd.purpose) values.purpose = String(fd.purpose);
    if (fd.company_type) values.company_type = String(fd.company_type);
    if (fd.company_sub_type) values.company_sub_type = String(fd.company_sub_type);
    for (let n = 1; n <= 3; n++) {
      const nature = fd[`business_nature_${n}`];
      const code = fd[`msic_code_${n}`];
      if (nature) values[`business_nature_${n}`] = String(nature);
      if (code) values[`msic_code_${n}`] = String(code);
    }
  },
  { deep: true, immediate: true },
);

watch(
  values,
  () => {
    nextTick(() => docRef.value?.relayoutPages?.());
  },
  { deep: true },
);

/** T-Link / incorporation 선택 → 상세 조회 후 live DOC 자동 채움 */
const lastAutofillIcp = ref('');
/** 자동 채움으로 기록한 키 (진단/추적용) */
const autofilledKeys = ref<Set<string>>(new Set());

/**
 * 회사 링크 해제(Change → Skip / 선택 취소) 시 문서를 빈 상태로 되돌린다.
 * 자동채움뿐 아니라 chatFormData(formData) 경유로 채워진 값도 있어 values 전체를 비운다.
 */
function resetAutofilledValues() {
  for (const k of Object.keys(values)) {
    delete values[k];
  }
  autofilledKeys.value = new Set();
  lastAutofillIcp.value = '';
  void nextTick(() => {
    docRef.value?.restoreLocalState?.({ directorRows: [1], memberRows: [1] });
    docRef.value?.relayoutPages?.();
    emitProgress();
  });
}

async function resolveActivityNameByCode(incData: Record<string, any>): Promise<Map<string, string>> {
  const codes = new Set<string>();
  for (const raw of [
    incData.primaryActivity,
    incData.secondaryActivity,
    incData.tertiaryActivity,
    incData.msic_1,
    incData.msic_2,
    incData.msic_3,
    incData.primaryActivityCode,
    incData.secondaryActivityCode,
  ]) {
    const s = String(raw || '').trim();
    if (/^\d{4,5}$/.test(s)) codes.add(s);
  }
  if (!codes.size) return new Map();

  try {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.user?.customer?.jurisdiction || '').toUpperCase();
    let codeList: Array<{ code: string; name: string }> = [];

    if (jurisdiction === 'MY' || jurisdiction === 'MALAYSIA' || !jurisdiction) {
      const res = (await entityMyApi.getMalaysiaBusinessCodes({ crsFilableOnly: true })) as any;
      const raw = res?.data?.data || res?.data || [];
      codeList = Array.isArray(raw)
        ? raw.map((c: any) => ({
            code: String(c.code || c.TOTAL_CODE || c.msicCode || '').trim(),
            name: String(c.name || c.CODE_NAME || c.description || '').trim(),
          }))
        : [];
    } else {
      const res = (await entityMyApi.getSSICList()) as any;
      const codesArr = res?.data?.codes || res?.data?.data?.codes || [];
      codeList = Array.isArray(codesArr)
        ? codesArr.map((c: any) => ({
            code: String(c.code || c.TOTAL_CODE || '').trim(),
            name: String(c.name || c.CODE_NAME || '').trim(),
          }))
        : [];
    }

    const map = new Map<string, string>();
    for (const item of codeList) {
      if (item.code && codes.has(item.code) && item.name) map.set(item.code, item.name);
    }
    return map;
  } catch (err) {
    console.warn('[IncorporationDoc] MSIC name resolve failed:', err);
    return new Map();
  }
}

async function autofillFromIncorporation(icpCd: string) {
  const cd = String(icpCd || '').trim();
  if (!cd || cd === lastAutofillIcp.value) return;
  lastAutofillIcp.value = cd;
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/entity-data/${cd}`, {
      credentials: 'include',
    });
    const json = await res.json();
    if (!json?.success) return;
    const incData = json.data?.incorporationData || {};
    // entity-data 요약 directors/shareholders 를 원본에 보강 (이름·국적 등)
    if ((!incData.directors || !incData.directors.length) && json.data?.directors?.length) {
      incData.directors = json.data.directors;
    }
    if ((!incData.shareholders || !incData.shareholders.length) && json.data?.shareholders?.length) {
      incData.shareholders = json.data.shareholders;
    }
    if (!incData.companyName && json.data?.companyName) {
      incData.companyName = json.data.companyName;
    }
    const activityNameByCode = await resolveActivityNameByCode(incData);
    const mapped = mapIncorporationToIncDocValues(incData, { activityNameByCode });
    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      if (v) {
        values[k] = v;
        written.add(k);
      }
    }

    // ── CDD 인스턴스 자동 생성 (사람마다, 개인/법인 유형별로 1부) ──
    try {
      const tmplJson = await fetch('/api/aiDocuments/incorporation/templates-with-variables', { credentials: 'include' }).then((r) => r.json());
      const templates = tmplJson?.templates || [];
      const schemaOf = (docType: CddDocType) => {
        const dt = { individual: 'TMP006', legal_person: 'TMP004', legal_arrangement: 'TMP005' }[docType];
        const tmpl = templates.find((t: any) => t.docType === dt);
        return tmpl ? (tmpl.variables || []).map((v: any) => ({ varKey: v.varKey || v, varLabel: v.varLabel || v, varType: v.varType || 'text', isRequired: v.isRequired ?? true })) : null;
      };
      const CORP_RE = /\b(sdn|bhd|berhad|ltd|limited|pte|corp|company|inc|plc|llp)\b/i;
      // 이사/멤버 이름 추출 — 매퍼 personName 과 동일(corporateName / lastName+firstName / name / fullName).
      // 이름 조립 순서는 시스템 전체(T-Link·entity-data·DI)와 같게 **이름 → 성** 이다.
      // [lastName, firstName] 로 붙이면 'AHMAD BIN FULLCASE' 가 'BIN FULLCASE AHMAD' 로 뒤집혀
      // CDD 칩·문서에만 다른 이름이 찍혔다(실측).
      // 또 SSM 미등록 법인(외국법인·정부기관)은 corporateName 이 비고 entityName 에만 이름이 있다.
      // 이걸 안 보면 이름이 빈 문자열이 되고, 아래 dedup 에서 그대로 **탈락해 CDD 가 누락**된다.
      const pName = (p: any) => String(
        // 법인명 우선(법인은 firstName/lastName 가 비어 있다).
        p?.corporateName || p?.myShareholderDetails?.corporateName || p?.myDetails?.corporateName
        || p?.entityName || p?.myShareholderDetails?.entityName || p?.myDetails?.entityName
        // 개인은 T-Link 가 편집하는 firstName+lastName 를 그대로 쓴다(= 화면·문서 일치).
        // 저장본 p.name 은 과거 2칸 입력 때 뒤집혀 캐시된 값이 섞여 있어 폴백으로만 쓴다.
        || [p?.firstName, p?.lastName].filter(Boolean).join(' ')
        || p?.name
        || p?.fullName || '',
      ).trim();
      const isCorp = (p: any) => !!(
        p?.corporateName || p?.entityName
        || p?.myShareholderKind === 'BodyCorporate'
        || p?.isCorporate || p?.entityType === 'CORPORATE' || CORP_RE.test(pName(p))
      );
      const rawDirs = (incData.directors || []);
      const rawShs = (incData.shareholders || []);
      // 미등록 법인은 SSM 등록번호가 없고 entityNo 를 쓴다 — 이게 빠지면 식별자까지 빈값이 된다.
      const idOf = (p: any) => String(p?.idNo || p?.nricNo || p?.passportNo || p?.contactRegNo || p?.registrationNo || p?.identificationNo || p?.entityNo || p?.compRegNo || '').replace(/[\s-]/g, '').toUpperCase();
      const dirs = rawDirs.map((d: any) => ({ name: pName(d), nationality: d.nationality, isForeign: d.isForeign, email: d.email, idNo: d.idNo, idType: d.idType, contactCd: '' }));
      const shs = rawShs.map((s: any) => ({ name: pName(s), email: s.email, idNo: s.idNo, shares: s.numberOfShares, shareType: s.classOfShare, contactCd: '' }));
      const qa: any = { icpCd: cd, companyName: incData.companyName || values.company_name || '', directors: dirs, shareholders: shs, useStandardTemplates: true };

      // T-Link 인물 목록(원본 필드 유지 + 표시용 name/positionName/contactRegNo + 초기 autofill 행 prefix).
      tlinkPersons.value = [
        ...rawDirs.map((d: any, i: number) => ({ ...d, name: pName(d), positionName: 'Director', contactRegNo: d.contactRegNo || d.idNo || d.nricNo || d.passportNo || '', nationalityName: d.nationalityName || d.nationality || '', _srcPrefix: `dir${i + 1}_` })),
        ...rawShs.map((s: any, i: number) => ({ ...s, name: pName(s), positionName: isCorp(s) ? 'Corporate Member' : 'Member', contactRegNo: s.contactRegNo || s.idNo || s.nricNo || s.passportNo || '', nationalityName: s.nationalityName || s.nationality || '', _srcPrefix: `mem${i + 1}_` })),
      ];

      // ✨ 선택 시 재사용할 매핑 컨텍스트 저장.
      cddMapCtx.value = { incData, qa, schemas: { individual: schemaOf('individual'), legal_person: schemaOf('legal_person'), legal_arrangement: schemaOf('legal_arrangement') } };

      cddDocs.value = []; cddSeq = 0;
      const norm = (n: string) => n.trim().toLowerCase().replace(/\s+/g, ' ');
      const seen = new Set<string>();
      const persons: Array<{ name: string; ctx: 'director' | 'shareholder'; idx: number; legal: boolean; pid: string }> = [
        ...dirs.map((d: any, i: number) => ({ name: pName(d), ctx: 'director' as const, idx: i, legal: isCorp(d), pid: idOf(rawDirs[i]) || idOf(d) })),
        ...shs.map((s: any, i: number) => ({ name: pName(s), ctx: 'shareholder' as const, idx: i, legal: isCorp(s), pid: idOf(rawShs[i]) || idOf(s) })),
      ];
      console.info('[IncorporationPre] CDD seed — persons:', persons.length, 'templates:', templates.length, 'dirs:', dirs.length, 'shs:', shs.length);
      for (const p of persons) {
        // 같은 사람(디렉터이자 멤버) → CDD 1부만. ID(NRIC/여권) 우선, 없으면 이름으로 중복제거.
        const dedupKey = p.pid ? `id:${p.pid}` : `nm:${norm(p.name)}`;
        if ((!p.pid && !norm(p.name)) || seen.has(dedupKey)) continue; seen.add(dedupKey);
        const docType: CddDocType = p.legal ? 'legal_person' : 'individual';
        const schema = schemaOf(docType);
        const inst: CddInstance = { id: ++cddSeq, docType, label: CDD_DOCS[docType].label, values: {}, checkbox: '', shRowCount: 2, boRowCount: 2 };
        try {
          // 스키마 있으면 autoMap 시드, 없어도(템플릿 미확보) 인스턴스는 생성하고 최소값만 채운다.
          const fd = schema ? autoMapVariables(schema, incData, qa, p.ctx as any, p.idx) : {};
          for (const [k, v] of Object.entries(fd)) { if (v && typeof v === 'string' && !v.startsWith('[')) inst.values[k] = v; }
        } catch (perr) {
          console.warn('[IncorporationPre] CDD autoMap failed for', p.name, perr);
        }
        // autoMap 이 이름을 못 채웠으면 원본 이름으로 보충
        const nameKey = docType === 'legal_person' ? 'lp_company_name' : 'ind_full_name';
        if (!String(inst.values[nameKey] || '').trim()) inst.values[nameKey] = p.name;
        cddDocs.value.push(inst);
      }
      console.info('[IncorporationPre] CDD instances created:', cddDocs.value.length);
      if (cddDocs.value.length) activeCddId.value = cddDocs.value[0].id;
      // legal_person 인스턴스에 주주/BO 표 채움
      for (const inst of cddDocs.value.filter((d) => d.docType === 'legal_person')) {
        await autofillShareholdersBoInto(inst, cd).catch(() => {});
      }
    } catch (e) {
      console.warn('[IncorporationPre] CDD instance autofill failed:', e);
    }

    // ── Section 201 인스턴스 — 선임 이사별 Declaration(기본) 자동생성(초기 autofill 의 dir 행 값 사용) ──
    try {
      s201Docs.value = []; s201Seq = 0;
      const dirRows: number[] = Array.isArray(mapped.directorRows) ? mapped.directorRows : [];
      for (const rid of dirRows) {
        const nm = String(values[`dir${rid}_name`] || '').trim();
        if (!nm) continue;
        const id = ++s201Seq;
        s201Docs.value.push({ id, docType: 'declaration', label: S201_TYPE_META.declaration.label, values: { s201_name: nm, s201_nric: String(values[`dir${rid}_id_no`] || '') } });
      }
      if (s201Docs.value.length) activeS201Id.value = s201Docs.value[0].id;
    } catch (e) {
      console.warn('[IncorporationPre] Section 201 instance autofill failed:', e);
    }

    autofilledKeys.value = written;
    await nextTick();
    docRef.value?.restoreLocalState?.({
      directorRows: mapped.directorRows,
      memberRows: mapped.memberRows,
    });
    await nextTick();
    docRef.value?.relayoutPages?.();
    emitProgress();
  } catch (err) {
    console.warn('[IncorporationDoc] autofill failed:', err);
    lastAutofillIcp.value = '';
  }
}

/** CDD Legal Person 인스턴스에 주주/BO 표 자동채움 — 엔티티 멤버/BO 조회 → inst.values. */
async function autofillShareholdersBoInto(inst: CddInstance, ecd: string) {
  if (!ecd) return;
  const [memRes, boRes] = await Promise.all([
    getActiveMembers(ecd).catch(() => null),
    getActiveBeneficialOwners(ecd).catch(() => null),
  ]);
  const members = (memRes as any)?.data?.members || (memRes as any)?.members || [];
  const bos = (boRes as any)?.data?.beneficialOwners || (boRes as any)?.beneficialOwners || [];
  const shRows = buildShareholderRows(members).slice(0, CDD_SHAREHOLDER_MAX);
  const boRows = buildBoRows(bos, shRows.map((r: any) => r.name)).slice(0, CDD_SHAREHOLDER_MAX);
  const v = inst.values;
  if (shRows.length) {
    shRows.forEach((r: any, i: number) => {
      v[`shareholder_name_${i + 1}`] = r.name; v[`share_type_${i + 1}`] = r.shareType; v[`share_pct_${i + 1}`] = r.pct;
    });
    inst.shRowCount = Math.max(2, shRows.length);
  }
  if (boRows.length) {
    boRows.forEach((r: any, i: number) => { v[`bo_name_${i + 1}`] = r.name; v[`bo_relationship_${i + 1}`] = r.relationship; });
    inst.boRowCount = Math.max(1, boRows.length);
  }
  const smNames = [...new Set((Array.isArray(bos) ? bos : [])
    .filter((b: any) => b?.isSeniorManagement)
    .map((b: any) => String(b?.name || b?.contactName || b?.fullName || '').trim())
    .filter(Boolean))];
  if (smNames.length && !String(v.lp_senior_management || '').trim()) v.lp_senior_management = smNames.join(', ');
}

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      void autofillFromIncorporation(cd);
    } else if (prev) {
      // Change 로 회사 해제(선택 취소/Skip) → 자동 채운 값 초기화
      resetAutofilledValues();
    }
  },
  { immediate: true },
);

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const editBaseline = ref<string | null>(null);
const taskConfigSteps = ref<string[]>([]);

onMounted(async () => {
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    // L_TASK_CONFIG bit → API processSteps: ['Doc', 'T-Sign', 'e-Filing']
    taskConfigSteps.value = Array.isArray(cfg?.processSteps)
      ? cfg.processSteps
      : Array.isArray((cfg as any)?.steps)
        ? (cfg as any).steps
        : [];
  } catch {
    /* ignore */
  }
  await nextTick();
  if (editMode.value) {
    try {
      editBaseline.value = JSON.stringify(gatherState());
    } catch {
      editBaseline.value = null;
    }
  }
});

function emitProgress() {
  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  const dirIds: number[] =
    Array.isArray(local.directorRows) && local.directorRows.length ? local.directorRows : [1];
  const memIds: number[] =
    Array.isArray(local.memberRows) && local.memberRows.length ? local.memberRows : [1];
  const keys = progressKeysForRows(dirIds, memIds);
  const filled = keys.filter((k) => !!String(values[k] || '').trim()).length;
  const pct = keys.length ? Math.round((filled / keys.length) * 100) : 0;
  emit('progress-change', pct);
}
watch(values, () => emitProgress(), { deep: true });
watch(
  () => (docRef.value as any)?.directorRows,
  () => emitProgress(),
  { deep: true },
);

const availableTemplates = computed(() =>
  (props.selection?.documents || []).filter((d) => d.tmpId),
);

type DocKind = 'section14' | 'section201Attestation' | 'section201' | 'cddIndividual' | 'cddLegalPerson' | 'cddLegalArrangement' | 'other';
function classifyDoc(name: string, docCode?: string): DocKind {
  const n = String(name || '');
  const c = String(docCode || '').toUpperCase();
  // CDD 우선 판정 (DOC_TYPE: TMP004=Legal Person, TMP005=Legal Arrangement, TMP006=Individual)
  if (c === 'TMP006' || /legal\s*arrangement/i.test(n) === false && /due\s*diligence.*individual|cdd.*individual|\(individual\)/i.test(n)) return 'cddIndividual';
  if (c === 'TMP004' || /legal\s*person|\(legal\s*person\)/i.test(n)) return 'cddLegalPerson';
  if (c === 'TMP005' || /legal\s*arrangement|\(legal\s*arrangement\)/i.test(n)) return 'cddLegalArrangement';
  if (DOC_SHEET_PATTERNS.section14.test(n)) return 'section14';
  if (DOC_SHEET_PATTERNS.section201Attestation.test(n)) return 'section201Attestation';
  if (DOC_SHEET_PATTERNS.section201.test(n)) return 'section201';
  return 'other';
}

/** Section 201 — 템플릿 실제 varKey 에 autoMap + live DOC 별칭 병합 */
async function buildSection201FormData(opts: {
  templateId: number;
  directorIndex: number;
  focusDirectorId: number;
  valuesSnap: Record<string, string>;
  directorIds: number[];
  memberIds: number[];
  incorporationData: Record<string, any>;
  tmplVarsById: Map<number, Array<{ varKey: string; varLabel: string }>>;
  signersForDoc: any[];
}): Promise<Record<string, any>> {
  const {
    templateId,
    directorIndex,
    focusDirectorId,
    valuesSnap,
    directorIds,
    memberIds,
    incorporationData,
    tmplVarsById,
    signersForDoc,
  } = opts;

  const aliases = buildIncorporationDocFormData({
    values: valuesSnap,
    directorIds,
    memberIds,
    focusDirectorId,
    signersForDoc,
  });

  const schema = tmplVarsById.get(templateId) || [];
  if (!schema.length) return aliases;

  const qaDirectors = directorIds.map((id) => ({
    name: String(valuesSnap[`dir${id}_name`] || '').trim(),
    idNo: String(valuesSnap[`dir${id}_id_no`] || '').trim(),
    idType: String(valuesSnap[`dir${id}_id_type`] || '').trim(),
    nationality: String(valuesSnap[`dir${id}_nationality`] || '').trim(),
    email: String(valuesSnap[`dir${id}_email`] || '').trim(),
    isForeign: isForeignDirector(valuesSnap, id),
    contactCd: '',
  }));
  const qa = {
    companyName: String(valuesSnap.company_name || '').trim(),
    directors: qaDirectors,
    shareholders: memberIds.map((id) => ({
      name: String(valuesSnap[`mem${id}_name`] || '').trim(),
      email: String(valuesSnap[`mem${id}_email`] || '').trim(),
      contactCd: '',
    })),
  } as any;

  // live DOC id 가 있으면 raw director 에도 주입 — autoMap 이 details/idNo 양쪽에서 찾도록
  const rawDirs = Array.isArray(incorporationData?.directors)
    ? incorporationData.directors.map((d: any) => ({ ...d }))
    : [];
  for (let i = 0; i < qaDirectors.length; i++) {
    const live = qaDirectors[i];
    if (!live?.idNo) continue;
    if (!rawDirs[i]) rawDirs[i] = { name: live.name };
    const row = rawDirs[i];
    if (!row.idNo) row.idNo = live.idNo;
    if (!row.identificationNo) row.identificationNo = live.idNo;
    const md = row.myDirectorDetails || row.myDetails || {};
    if (!md.idNumber) {
      row.myDirectorDetails = { ...md, idNumber: live.idNo, idType: live.idType || md.idType };
    }
  }
  const incForMap = { ...incorporationData, directors: rawDirs.length ? rawDirs : incorporationData?.directors };

  const mapped = autoMapVariables(schema, incForMap, qa, 'director', directorIndex);
  // live DOC 날짜/회사명 우선, autoMap 이 채운 템플릿 키 우선, 별칭은 빈 키 보충
  const merged: Record<string, any> = { ...aliases, ...mapped };
  if (valuesSnap.declaration_date) {
    merged.declaration_date = valuesSnap.declaration_date;
    merged.date_of_declaration = valuesSnap.declaration_date;
  }
  if (valuesSnap.company_name) {
    merged.company_name = valuesSnap.company_name;
    merged.proposed_company_name = valuesSnap.company_name;
  }
  // autoMap 이 ID 키를 못 채웠으면 별칭에서 보충
  const idFromLive = String(valuesSnap[`dir${focusDirectorId}_id_no`] || '').trim();
  if (idFromLive) {
    for (const v of schema) {
      const key = String(v.varKey || '').trim();
      if (!key) continue;
      const n = key.toLowerCase().replace(/[\s_-]+/g, '');
      const looksLikeId =
        /nric|passport|icpassport|idno|identification|contactid/.test(n) &&
        !/expiry|expire|type|date/.test(n);
      if (looksLikeId && !String(merged[key] || '').trim()) {
        merged[key] = idFromLive;
      }
    }
  }
  return merged;
}

function gatherState() {
  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  return { values: { ...values }, local, cddDocs: JSON.parse(JSON.stringify(cddDocs.value)), activeCddId: activeCddId.value };
}
function restoreState(s: any) {
  if (s?.values) Object.assign(values, s.values);
  if (Array.isArray(s?.cddDocs)) {
    cddDocs.value = s.cddDocs;
    cddSeq = cddDocs.value.reduce((m, d) => Math.max(m, d.id || 0), 0);
    activeCddId.value = s.activeCddId || cddDocs.value[0]?.id || 0;
  }
  if (s?.local) (docRef.value as any)?.restoreLocalState?.(s.local);
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  if (inputKey.value) commitInput();

  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  const directorIds: number[] =
    Array.isArray(local.directorRows) && local.directorRows.length ? local.directorRows : [1];
  const memberIds: number[] =
    Array.isArray(local.memberRows) && local.memberRows.length ? local.memberRows : [1];
  const valuesSnap = { ...values };

  if (!String(valuesSnap.company_name || '').trim()) {
    progress.value = '⚠ Please fill Company Name.';
    return;
  }
  if (!directorIds.some((id) => !!String(valuesSnap[`dir${id}_name`] || '').trim())) {
    progress.value = '⚠ Please fill at least one Director Name.';
    return;
  }

  const docs = availableTemplates.value;
  if (!docs.length) {
    progress.value = '⚠ No templates linked to this preset — cannot generate documents.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      directors: directorIds.map((id) => ({
        name: valuesSnap[`dir${id}_name`],
        idNo: valuesSnap[`dir${id}_id_no`],
        nationality: valuesSnap[`dir${id}_nationality`],
        isForeign: isForeignDirector(valuesSnap, id),
      })),
      members: memberIds.map((id) => ({
        name: valuesSnap[`mem${id}_name`],
        idNo: valuesSnap[`mem${id}_id_no`],
        shares: valuesSnap[`mem${id}_no_of_shares`],
      })),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    const section14 = docs.find((d) => classifyDoc(d.templateName || d.docCode || '') === 'section14');
    const s201 = docs.find((d) => classifyDoc(d.templateName || d.docCode || '') === 'section201');
    const s201a = docs.find(
      (d) => classifyDoc(d.templateName || d.docCode || '') === 'section201Attestation',
    );
    // fallback: unclassified templates by order
    const others = docs.filter(
      (d) => classifyDoc(d.templateName || d.docCode || '') === 'other',
    );

    // Section 201 — 템플릿 실제 placeholder + incorporation 원본으로 autoMap
    const tmplVarsById = new Map<number, Array<{ varKey: string; varLabel: string }>>();
    let incorporationData: Record<string, any> = {};
    try {
      const [tmplJson, entJson] = await Promise.all([
        fetch('/api/aiDocuments/incorporation/templates-with-variables', {
          credentials: 'include',
        }).then((r) => r.json()),
        entityCd.value
          ? fetch(`/api/aiDocuments/incorporation/entity-data/${entityCd.value}`, {
              credentials: 'include',
            }).then((r) => r.json())
          : Promise.resolve(null),
      ]);
      for (const t of tmplJson?.templates || []) {
        const tid = Number(t.templateId || t.tmpId || 0);
        if (!tid) continue;
        tmplVarsById.set(
          tid,
          (t.variables || []).map((v: any) => ({
            varKey: v.varKey || v,
            varLabel: v.varLabel || v.varKey || v,
          })),
        );
      }
      incorporationData = entJson?.data?.incorporationData || {};
    } catch (err) {
      console.warn('[IncorporationDOC] template/entity preload failed:', err);
    }

    if (section14?.tmpId) {
      docsToSave.push({
        templateId: section14.tmpId,
        label: String(section14.templateName || 'Section 14').replace(/\.docx?$/i, ''),
        personName: valuesSnap.company_name || 'Company',
        entityCd: entityCd.value || undefined,
        formData: buildIncorporationDocFormData({
          values: valuesSnap,
          directorIds,
          memberIds,
        }),
      });
    }

    // ── Section 201 — 추가된 인스턴스(선임 이사)마다 1부씩 생성 (Declaration / With Attestation) ──
    for (const inst of s201Docs.value) {
      const tmpl = inst.docType === 'attestation' ? s201a : s201;
      if (!tmpl?.tmpId) continue;
      const who = String(inst.values.s201_name || '').trim();
      const typeLabel = inst.docType === 'attestation' ? 'With Attestation' : 'Declaration';
      const formData: Record<string, any> = {
        company_name: valuesSnap.company_name || '',
        declaration_date: valuesSnap.declaration_date || '',
        date_of_declaration: valuesSnap.declaration_date || '',
        name: who,
        nric_passport_no: String(inst.values.s201_nric || ''),
      };
      docsToSave.push({
        templateId: tmpl.tmpId,
        label: `Section 201 (${typeLabel})${who ? ` — ${who}` : ''}`,
        personName: who || 'Director',
        entityCd: entityCd.value || undefined,
        formData,
        signers: who ? [{ name: who, title: 'Director', role: 'director' }] : [],
      });
    }

    // ── CDD — 추가된 인스턴스(사람/클라이언트)마다 1부씩 생성 ──
    const cddTmplByType: Record<string, any> = {
      individual: docs.find((d) => classifyDoc(d.templateName || d.docCode || '', d.docCode) === 'cddIndividual'),
      legal_person: docs.find((d) => classifyDoc(d.templateName || d.docCode || '', d.docCode) === 'cddLegalPerson'),
      legal_arrangement: docs.find((d) => classifyDoc(d.templateName || d.docCode || '', d.docCode) === 'cddLegalArrangement'),
    };
    for (const inst of cddDocs.value) {
      const tmpl = cddTmplByType[inst.docType];
      if (!tmpl?.tmpId) continue;
      const nameKey = inst.docType === 'individual' ? 'ind_full_name' : inst.docType === 'legal_person' ? 'lp_company_name' : 'la_name';
      const who = String(inst.values[nameKey] || '').trim();
      const signers = who ? [{ name: who, role: 'signer' as const }] : [];
      docsToSave.push({
        templateId: tmpl.tmpId,
        label: `CDD (${CDD_TYPE_META[inst.docType].label})${who ? ` — ${who}` : ''}`,
        personName: who || valuesSnap.company_name || 'Company',
        entityCd: entityCd.value || undefined,
        formData: buildCddVariables({ docType: inst.docType, values: inst.values, checkbox: inst.checkbox, shRowCount: inst.shRowCount, boRowCount: inst.boRowCount }),
        signers,
      });
    }

    // If no classified templates, dump all once
    if (!docsToSave.length && others.length) {
      for (const d of others) {
        if (!d.tmpId) continue;
        docsToSave.push({
          templateId: d.tmpId,
          label: String(d.templateName || 'Document').replace(/\.docx?$/i, ''),
          personName: valuesSnap.company_name || 'Company',
          entityCd: entityCd.value || undefined,
          formData: buildIncorporationDocFormData({
            values: valuesSnap,
            directorIds,
            memberIds,
          }),
        });
      }
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[IncorporationDOC] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[IncorporationDOC] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    } else {
      progress.value = '⚠ No documents to save.';
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }
    try {
      progress.value = 'Loading next screen…';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[IncorporationDOC] create failed', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="rsn-panel" :class="{ 'rsn-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Incorporation DOC — Section 14 / Section 201</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field
        v-model="titleModel"
        placeholder="e.g. Incorporation (Pre) — NewCo Ventures"
        variant="outlined"
        density="compact"
        hide-details
        class="ct-input"
      />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity (optional)' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">
        Change
      </v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">
        {{ labelize(inputKey) }}
        <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span>
      </div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field
              v-bind="mp"
              v-model="inputText"
              placeholder="DD/MM/YYYY"
              variant="outlined"
              density="compact"
              hide-details
              append-inner-icon="mdi-calendar"
            />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field
          v-else
          v-model="inputText"
          :placeholder="labelize(inputKey)"
          variant="outlined"
          density="compact"
          hide-details
          autofocus
          @keydown.enter="commitInput"
        />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">
          Skip
        </v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <IncorporationPreDoc ref="docRef" />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: /error|⚠/i.test(progress) }">
          {{ progress }}
        </span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >
          {{ editMode ? 'Update Task' : 'Create Task' }}
        </v-btn>
      </div>
    </div>

    <AddressInputModal
      v-model="addrModal"
      :label="addrLabel"
      :has-components="false"
      :initial="addrInitial"
      @apply="applyAddress"
    />
  </div>
</template>

<style scoped>
.rsn-panel { display:flex; flex-direction:column; }
.rsn-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.rsn-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; }
.co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; margin-bottom:6px; }
.co-inputbar .opt { font-weight:500; color:#8a94a6; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 8px 24px; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>

<style>
/* secbar↔표만 붙이고, 블록 사이(gap 이후)는 분리 */
.cdd-tbl { width:100%; border-collapse:collapse; margin-top:0; }
.rsn-secbar + .cdd-tbl { margin-top:-1px; }
.cdd-tbl td, .cdd-tbl th { border:1px solid #000; padding:5px 8px; vertical-align:top; text-align:left; font-weight:400; }
.cdd-lcell { width:38%; background:#f7f8fb; font-weight:700 !important; font-size:12px; }
</style>
